/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {Component, EventEmitter, Input, NgZone, OnDestroy, OnInit, Output, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {AbstractControl, UntypedFormBuilder, ValidatorFn, Validators} from '@angular/forms';
import {WarningsEnum} from '../../../shared/warnings/warnings.enum';
import {forkJoin, merge, Observable, of, ReplaySubject, Subscription, timer} from 'rxjs';
import {PartnerBaseModel} from '../../../api/ms-claims/contracts/partner-base.model';
import {SupplyingPackingPlantBaseModel} from '../../../api/ms-claims/contracts/supplying-packing-plant-base.model';
import {ClaimCodeBaseModel} from '../../../api/ms-claims/contracts/claim-code-base.model';
import {LoadingService} from '../../../shared/loading/loading-service/loading.service';
import {TypeaheadHelperService} from '../../../shared/services/typeahead-helper.service';
import {ReferenceDataApi} from '../../../api/ms-claims/reference-data.api';
import {MaterialApi} from '../../../api/ms-claims/material.api';
import {ClaimDetailModel} from '../../../api/ms-claims/contracts/claim-detail.model';
import {ClaimStatusEnum} from '../../../api/ms-claims/contracts/claim-status.enum';
import {InputTextComponent} from '../../../shared/input/input-text/input-text.component';
import {InputDropdownComponent} from '../../../shared/input/input-dropdown/input-dropdown.component';
import {ClaimInformationDataModel} from '../../models/claim-information-data.model';
import {InputAutocompleteComponent} from '../../../shared/input/input-autocomplete/input-autocomplete.component';
import {MaterialDetailModel} from '../../../api/ms-claims/contracts/material-detail.model';
import {InputTextareaComponent} from '../../../shared/input/input-textarea/input-textarea.component';
import {InputMessagesService} from '../../../shared/input/service/input-messages.service';
import {MandatoryMessageTypeEnum} from '../../../shared/input/service/mandatory-message-type.enum';
import {objectsValidator} from '../../../shared/input/input-validators';
import {InputComponent} from '../../../shared/input/input/input.component';
import {ClaimInformationMandatoryFieldsEnum} from './claim-information-mandatory-fields.enum';
import {TooltipEnum} from '../../../shared/tooltip/tooltip.enum';
import {FileManagerApi} from '../../../api/ms-filemanager/filemanager.api';
import {UploadFileModel} from '../models/upload-file.model';
import {ToastWrapperService} from '../../../shared/utils/toast-wrapper-service/toast-wrapper-service';
import trackByIndex from '../../../shared/utils/trackByIndex';
import { HttpEventType, HttpResponse } from '@angular/common/http';
import {UserService} from '../../../shared/services/user.service';
import {Permission} from '../../../shared/utils/permission.enum';
import {FileSummaryModel} from '../../../api/ms-filemanager/contracts/file-summary.model';
import {FileDownloadHelperService} from '../../services/file-download/file-download-helper.service';
import {FileManagerConfigurationModel} from '../../../api/ms-filemanager/contracts/file-manager-configuration.model';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {ExistingFileModalComponent} from '../../../shared/confirm-modal/deprecated-modals/existing-file-modal/existing-file-modal.component';
import {ExistingFileModalModel} from '../../../shared/confirm-modal/deprecated-modals/existing-file-modal/existing-file-modal.model';
import {AlertNotificationSeverity} from '../../../alert-notification-severity.model';
import {first} from 'rxjs/operators';
import {IS_TEST_ENVIRONMENT} from '../../../shared/utils/is-test-environment';

@Component({
    selector: 'app-claim-information-form',
    templateUrl: './claim-information-form.component.html',
    styleUrls: ['./claim-information-form.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ClaimInformationFormComponent implements OnInit, OnDestroy {
    @Input() hasTitle = true;
    /** Claim details to be showed in the input fields */
    @Input() claimDetail: ClaimDetailModel;
    @Input() editMode = false;
    /** Event emitter to output new/updated form values */
    @Output() updatedClaimInformation = new EventEmitter<ClaimInformationDataModel>();
    @Output() claimAttachments = new EventEmitter<FileSummaryModel[]>();
    @Output() validForSend = new EventEmitter<boolean>();
    @Output() validForSave = new EventEmitter<boolean>();
    @Output() isTouched = new EventEmitter<boolean>();
    @Output() isLoading = new EventEmitter<boolean>();
    @Output() mandatoryFieldsErrors = new EventEmitter<Map<ClaimInformationMandatoryFieldsEnum, TooltipEnum>>();
    @Output() attachmentsHaveChanged: EventEmitter<any> = new EventEmitter<any>();
    @Output() isFileManagerOnline = new EventEmitter<boolean>();
    /** Input variables */
    @ViewChild('partnerInput', {static: true}) partnerInput: InputDropdownComponent;
    @ViewChild('supplyingPackingPlantInput', {static: true}) supplyingPackingPlantInput: InputDropdownComponent;
    @ViewChild('materialInput', {static: true}) materialInput: InputAutocompleteComponent;
    @ViewChild('modelTypeInput', {static: true}) modelTypeInput: InputTextComponent;
    @ViewChild('modelCodeInput', {static: true}) modelCodeInput: InputTextComponent;
    @ViewChild('lotNumberInput', {static: true}) lotNumberInput: InputTextComponent;
    @ViewChild('mcdrNumberInput', {static: true}) mcdrNumberInput: InputTextComponent;
    @ViewChild('claimCodeInput', {static: true}) claimCodeInput: InputDropdownComponent;
    @ViewChild('subjectInput', {static: true}) subjectInput: InputTextComponent;
    @ViewChild('spoNumberInput', {static: true}) spoNumberInput: InputTextComponent;
    @ViewChild('descriptionInput', {static: true}) descriptionInput: InputTextareaComponent;
    /** Upload Message on Drag and Drop Area */
    public uploadAreaLabel: string;
    /** Input validators */
    validators: { [key: string]: ValidatorFn[] } = {
        partner: [objectsValidator],
        material: [objectsValidator],
        modelType: [Validators.maxLength(4), Validators.pattern(/^(?=.*[0-9])(?=.*[a-zA-Z])([a-zA-Z0-9]){4}$/)],
        lotNumber: [Validators.pattern(/^[0-9]{0,4}$/)],
        modelCode: [Validators.maxLength(3), Validators.pattern(/^[A-Za-z]\d{2}|^[A-Za-z]{2}\d$/)],
        mcdrNumber: [Validators.pattern(/^[0-9]{0,9}$/)],
        supplyingPackingPlant: [objectsValidator],
        claimCode: [objectsValidator],
        subject: [Validators.maxLength(40)],
        spoNumber: [Validators.pattern(/^[sS][0-9]{9}$/)],
        description: [Validators.maxLength(256)]
    };
    /** Mandatory fields errors variables */
    public mandatoryFieldsErrorsMap = new Map<ClaimInformationMandatoryFieldsEnum, TooltipEnum>();
    /** Input messages variables */
    public partnerInputMessages = new Map<WarningsEnum, string[]>();
    public supplyingPackingPlantInputMessages = new Map<WarningsEnum, string[]>();
    public materialInputMessages = new Map<WarningsEnum, string[]>();
    public modelTypeInputMessages = new Map<WarningsEnum, string[]>();
    public modelCodeInputMessages = new Map<WarningsEnum, string[]>();
    public lotNumberInputMessages = new Map<WarningsEnum, string[]>();
    public mcdrNumberInputMessages = new Map<WarningsEnum, string[]>();
    public claimCodeInputMessages = new Map<WarningsEnum, string[]>();
    public subjectInputMessages = new Map<WarningsEnum, string[]>();
    public spoNumberInputMessages = new Map<WarningsEnum, string[]>();
    public descriptionInputMessages = new Map<WarningsEnum, string[]>();
    public requiredAttachments = true;
    public isDeleting = false;

    public trackByIndex = trackByIndex;
    WarningEnum = WarningsEnum;
    ClaimStatusEnum = ClaimStatusEnum;
    Permission = Permission;
    public template = {
        /** Upload files variables */
        disableDragAndDrop: false,
        attachments: [] as UploadFileModel[],
    };
    private state = {
        /** file manger offline **/
        fileManagerOffline: true as boolean,
        /** file manger configuration **/
        fileManagerConfig: {} as FileManagerConfigurationModel,
        /** Claim information values variable */
        claimInformationValues: {} as ClaimInformationDataModel,
        /** Dropdown variables */
        partnerOptions: [] as PartnerBaseModel[],
        supplyingPackingPlantOptions: [] as SupplyingPackingPlantBaseModel[],
        claimCodeOptions: [] as ClaimCodeBaseModel[],
        /** Form is touched */
        isFormTouched: false,
        /** Subscriptions variable */
        subscriptions: new Subscription(),
        onFilesPending: new ReplaySubject<void>(1),
        onFilesDownload: new ReplaySubject<void>(1)
    };

    constructor(private formBuilder: UntypedFormBuilder,
                private loadingService: LoadingService,
                public typeahead: TypeaheadHelperService,
                private referenceDataApi: ReferenceDataApi,
                private materialsApi: MaterialApi,
                private inputMessagesService: InputMessagesService,
                private fileManagerApi: FileManagerApi,
                private toast: ToastWrapperService,
                public userService: UserService,
                private fileDownloadHelperService: FileDownloadHelperService,
                private ngZone: NgZone,
                private modal: NgbModal) {
        if (IS_TEST_ENVIRONMENT) {
            (window as any).UploadAttachment = (file: File): void => this.ngZone.run(
                    () => this.uploadFile(file));
        }

        this.loadingService.start();
    }

    ngOnInit(): void {
        /** Get the initial dropdowns data */
        this.getInitialData(this.initPartner);

        timer(0).subscribe(() => {
            /** Set the initial input values */
            this.setInitialFormValues(this.claimDetail);
            /** Disable inputs according to the Claim status */
            this.toggleInputFields(this.claimDetail);
        });
    }

    /**
     * Sets the partner value in case there's only 1 option
     */
    initPartner = (): void => {
        if (this.state.partnerOptions.length === 1) {
            this.partnerInput.setValue(this.state.partnerOptions[0]);
            this.partnerInput.disable();
        }
    };

    ngOnDestroy(): void {
        this.state.onFilesPending.complete();
        this.state.subscriptions.unsubscribe();
    }

    /**
     * Gets the initial data for the dropdowns and stops loading spinner
     */
    getInitialData(callback: () => void = (): void => {
    }): void {
        if (this.showAttachments()) {
            if (this.editMode && this.claimDetail.attachmentDirectory) {
                this.referenceDataApi.getActivePartners().subscribe((partners) => {
                    this.state.partnerOptions = partners;
                    this.cloneFileManagerData();
                }).add(() => {
                    callback();
                });
            } else {
                this.referenceDataApi.getActivePartners().subscribe((partners: PartnerBaseModel[]) => {
                    this.state.partnerOptions = partners;
                    this.getNewFileManagerData();
                }).add(() => {
                    callback();
                });
            }
        } else {
            forkJoin([
                this.referenceDataApi.getActivePartners()
            ]).subscribe(([partners]: [
                PartnerBaseModel[]
            ]): void => {
                this.state.partnerOptions = partners;
            }).add(() => {
                /** Stop spinner loading*/
                this.loadingService.stop();
                callback();
            });
        }
    }

    cloneFileManagerData(): void {
        forkJoin([
            this.fileManagerApi.cloneDirectory(this.claimDetail.attachmentDirectory),
            this.fileManagerApi.getDirectorySummary(this.claimDetail.attachmentDirectory),
            this.fileManagerApi.getFileManagerConfigurations()
        ]).subscribe(([directory, summary, config]: [
            string,
            FileSummaryModel[],
            FileManagerConfigurationModel
        ]): void => {
            this.state.fileManagerOffline = false;
            this.state.fileManagerConfig = config;

            this.state.claimInformationValues.attachmentDirectory = directory;
            this.updatedClaimInformation.emit(this.state.claimInformationValues);
            this.isFileManagerOnline.emit(!this.state.fileManagerOffline);

            this.template.attachments = summary.map(file => ({
                file: new File([new Uint8Array(file.size)], file.name),
                finished: true,
                event: new HttpResponse({status: 200}),
                isPrevious: true
            } as UploadFileModel));

            this.toggleDragAndDrop();
            this.validForSave.emit(this.isValidForSave());
            this.validForSend.emit(this.isValidForSend());
        }, error => {
            this.state.claimInformationValues.attachmentDirectory = this.claimDetail.attachmentDirectory;
            this.updatedClaimInformation.emit(this.state.claimInformationValues);
            this.toast.error([
                '<p>Orcan is not being able to provide the previously attached files list at the moment.</p>',
                '<p>Still, you can edit the claim information and the position details.</p>',
                '<p><strong>Please use the refresh button to reattempt a communication with the server.</strong></p>',
            ].join('<br>'), 'Error');
        }).add(() => {
            this.labelUploadArea(this.state.fileManagerConfig);
            /** Stop spinner loading*/
            this.loadingService.stop();
        });
    }

    getNewFileManagerData(): void {
        forkJoin([
            this.fileManagerApi.createNewDirectory(),
            this.fileManagerApi.getFileManagerConfigurations()
        ]).subscribe(([directory, config]: [
            string,
            FileManagerConfigurationModel
        ]): void => {
            this.state.fileManagerOffline = false;
            this.state.fileManagerConfig = config;
            this.state.claimInformationValues.attachmentDirectory = directory;
        }, error => {
            if (this.editMode) {
                this.toast.error([
                    '<p>Orcan is not being able to provide the previously attached files list at the moment.</p>',
                    '<p>Still, you can edit the claim information and the position details.</p>',
                    '<p><strong>Please use the refresh button to reattempt a communication with the server.</strong></p>',
                ].join('<br>'), 'Error');
            } else {
                this.toast.error([
                    '<p>Orcan is not being able to provide the files uploading service at the moment.</p>',
                    '<p><strong>Please use the refresh button to reattempt a communication with the server.</strong></p>',
                ].join('<br>'), 'Error');
            }

        }).add(() => {
            this.toggleDragAndDrop();
            this.updatedClaimInformation.emit(this.state.claimInformationValues);
            this.labelUploadArea(this.state.fileManagerConfig);
            /** Stop spinner loading*/
            this.loadingService.stop();
        });
    }

    refreshClick(): void {
        this.loadingService.start();
        if (this.editMode && this.claimDetail.attachmentDirectory) {
            this.cloneFileManagerData();
        } else {
            this.getNewFileManagerData();
        }
    }

    /**
     *  Sets the form values
     *  @param claimDetail: Claim detail information
     */
    setInitialFormValues = (claimDetail: ClaimDetailModel): void => {
        if (claimDetail) {
            this.partnerInput.setValue(claimDetail.partner);
            this.supplyingPackingPlantInput.setValue(claimDetail.supplyingPackingPlant);
            this.materialInput.setValue(claimDetail.material);
            this.modelTypeInput.setValue(claimDetail.modelType);
            this.modelCodeInput.setValue(claimDetail.modelCode);
            this.lotNumberInput.setValue(claimDetail.lotNumber);
            this.mcdrNumberInput.setValue(claimDetail.mcdrNumber);
            this.requiredAttachments = claimDetail.claimCode ? claimDetail.claimCode.requiresAttachments : true;
            this.claimCodeInput.setValue(claimDetail.claimCode);
            this.subjectInput.setValue(claimDetail.subject);
            this.spoNumberInput.setValue(claimDetail.spoNumber);
            this.descriptionInput.setValue(claimDetail.description);
        }
    };

    toggleInputFields = (claimDetail: ClaimDetailModel): void => {
        if (claimDetail) {
            if (claimDetail.status.code === ClaimStatusEnum.SUBMITTED) {
                this.partnerInput.disable();
                this.supplyingPackingPlantInput.disable();
                this.materialInput.disable();
                this.claimCodeInput.disable();
                this.modelTypeInput.disable();
                this.modelCodeInput.disable();
                this.lotNumberInput.disable();
                this.mcdrNumberInput.disable();
                this.subjectInput.disable();
                this.spoNumberInput.disable();
                this.descriptionInput.disable();
            } else if (claimDetail.status.code === ClaimStatusEnum.DRAFT) {
                this.partnerInput.disable();
            }
        } else {
            this.supplyingPackingPlantInput.disable();
            this.materialInput.disable();
            this.claimCodeInput.disable();
        }
    };

    /**
     * Manages the form state
     * If the form was not previously touched emits {@link formIsTouched} and updates {@link isFormTouched} value
     * Emits {@link validForSave} and {@link isValidForSend}
     * @param state The interacted abstract control
     */
    manageFormState(state: AbstractControl): void {
        if (state.dirty && !this.state.isFormTouched) {
            this.isTouched.emit(state.dirty);
            this.state.isFormTouched = state.dirty;
        }
        this.validForSave.emit(this.isValidForSave());
        this.validForSend.emit(this.isValidForSend());
    }

    /**
     * Sets the new partner value
     * Calls {@link updatedClaimInformation.emit()} to emit new header values
     */
    setPartnerValue(partner: PartnerBaseModel): void {
        this.state.claimInformationValues.partner = partner;
        this.updatedClaimInformation.emit(this.state.claimInformationValues);

        this.supplyingPackingPlantAfterParentChange();
        if (!this.partnerInput.isDisabled) {
            this.dependencyCheck(this.partnerInput, partner);
        }
    }

    managePartnerInputStateChange(partner: AbstractControl): void {
        this.manageFormState(partner);
        this.inputMessagesService.mandatoryWarnings(partner,
                this.partnerInputMessages,
                MandatoryMessageTypeEnum.SAVE_AND_SEND);
        this.inputMessagesService.invalidInputValueError(partner,
                this.partnerInputMessages,
                this.partnerInput.label);
        this.partnerInputMessages = new Map(this.partnerInputMessages);
        this.dependencyCheck(this.partnerInput, partner.value);
        this.manageMandatoryFieldsErrorsMap(
                ClaimInformationMandatoryFieldsEnum.PARTNER,
                partner.value,
                partner.value ? partner.value.code : null,
                this.partnerInput
        );
    }

    /**
     * Sets the new supplying packing plant value
     * Calls {@link manageMaterialInputAfterPlantChange}
     * Calls {@link updatedClaimInformation.emit()} to emit new header values
     */
    setSupplyingPackingPlantValue(plant: SupplyingPackingPlantBaseModel): void {
        this.state.claimInformationValues.supplyingPackingPlant = plant;
        this.updatedClaimInformation.emit(this.state.claimInformationValues);

        this.manageClaimCodeInputAfterParentChange();
        this.manageMaterialInputAfterPlantChange();
        this.dependencyCheck(this.supplyingPackingPlantInput, plant);
    }

    /**
     * Manages the supplying packing plant input status changes, adding errors and warnings as needed
     */
    manageSupplyingPackingPlantInputStateChange(plant: AbstractControl): void {
        this.manageFormState(plant);
        this.inputMessagesService.mandatoryWarnings(plant,
                this.supplyingPackingPlantInputMessages,
                MandatoryMessageTypeEnum.SAVE_AND_SEND);
        this.inputMessagesService.invalidInputValueError(plant,
                this.supplyingPackingPlantInputMessages,
                this.supplyingPackingPlantInput.label);
        this.supplyingPackingPlantInputMessages = new Map(this.supplyingPackingPlantInputMessages);
        this.dependencyCheck(this.supplyingPackingPlantInput, plant.value);
        this.manageMandatoryFieldsErrorsMap(
                ClaimInformationMandatoryFieldsEnum.PLANT,
                plant.value,
                plant.value ? plant.value.code : null,
                this.supplyingPackingPlantInput
        );
    }

    /**
     * Sets the new material value
     * Calls {@link updatedClaimInformation.emit()} to emit new header values
     */
    setMaterialValue(material: MaterialDetailModel): void {
        this.state.claimInformationValues.material = material;
        this.updatedClaimInformation.emit(this.state.claimInformationValues);
    }

    /**
     * Manages the material input status changes, adding errors and warnings as needed
     */
    manageMaterialInputStateChange(material: AbstractControl): void {
        this.manageFormState(material);
        this.inputMessagesService.mandatoryWarnings(material,
                this.materialInputMessages,
                MandatoryMessageTypeEnum.SAVE_AND_SEND);
        this.inputMessagesService.invalidInputValueError(material,
                this.materialInputMessages,
                this.materialInput.label);
        this.materialInputMessages = new Map(this.materialInputMessages);
        this.manageMandatoryFieldsErrorsMap(
                ClaimInformationMandatoryFieldsEnum.MATERIAL,
                material.value,
                material.value ? material.value.number : null,
                this.materialInput
        );
    }

    /**
     * Sets the new model code value
     * Calls {@link updatedClaimInformation.emit()} to emit new header values
     */
    setModelCodeValue(modelCode: string): void {
        this.state.claimInformationValues.modelCode = modelCode;
        this.updatedClaimInformation.emit(this.state.claimInformationValues);
    }

    /**
     * Manages the model code input status changes, adding errors and warnings as needed
     */
    manageModelCodeChange(modelCode: AbstractControl): void {
        this.manageFormState(modelCode);
        this.inputMessagesService.invalidInputValueError(modelCode,
                this.modelCodeInputMessages,
                this.modelCodeInput.label,
                'G01');
        this.modelCodeInputMessages = new Map(this.modelCodeInputMessages);
    }

    /**
     * Sets the new model type value
     * Calls {@link updatedClaimInformation.emit()} to emit new header values
     */
    setModelTypeValue(modelType: string): void {
        this.state.claimInformationValues.modelType = modelType;
        this.updatedClaimInformation.emit(this.state.claimInformationValues);
    }

    /**
     * Manages the model type input status changes, adding errors and warnings as needed
     */
    manageModelTypeChange(modelType: AbstractControl): void {
        this.manageFormState(modelType);
        this.inputMessagesService.invalidInputValueError(modelType,
                this.modelTypeInputMessages,
                this.modelTypeInput.label,
                'KE98');
        this.modelTypeInputMessages = new Map(this.modelTypeInputMessages);
    }

    /**
     * Sets the new lot number value
     * Calls {@link updatedClaimInformation.emit()} to emit new header values
     */
    setLotNumberValue(lotNumber: string): void {
        this.state.claimInformationValues.lotNumber = lotNumber;
        this.updatedClaimInformation.emit(this.state.claimInformationValues);
    }

    /**
     * Manages the lot number input status changes, adding errors and warnings as needed
     */
    manageLotNumberChange(lotNumber: AbstractControl): void {
        this.manageFormState(lotNumber);
        this.inputMessagesService.invalidInputValueError(lotNumber,
                this.lotNumberInputMessages,
                this.lotNumberInput.label);
        this.lotNumberInputMessages = new Map(this.lotNumberInputMessages);
    }

    /**
     * Sets the new mcdr number value
     * Calls {@link updatedClaimInformation.emit()} to emit new header values
     */
    setMcdrNumberValue(mcdrNumber: string): void {
        this.state.claimInformationValues.mcdrNumber = mcdrNumber;
        this.updatedClaimInformation.emit(this.state.claimInformationValues);
    }

    /**
     * Manages the mcdr number input status changes, adding errors and warnings as needed
     */
    manageMcdrNumberChange(mcdr: AbstractControl): void {
        this.manageFormState(mcdr);
        this.inputMessagesService.invalidInputValueError(mcdr,
                this.mcdrNumberInputMessages,
                this.mcdrNumberInput.label);
        this.mcdrNumberInputMessages = new Map(this.mcdrNumberInputMessages);
    }

    /**
     * Sets the new spo number value
     * Calls {@link updatedClaimInformation.emit()} to emit new header values
     */
    setSPONumberValue(spoNumber: string): void {
        this.state.claimInformationValues.spoNumber = spoNumber?.toUpperCase();
        this.updatedClaimInformation.emit(this.state.claimInformationValues);
    }

    /**
     * Manages the spo number input status changes, adding errors and warnings as needed
     */
    manageSPONumberChange(spoNumber: AbstractControl): void {
        this.manageFormState(spoNumber);
        this.inputMessagesService.invalidInputValueError(spoNumber,
                this.spoNumberInputMessages,
                this.spoNumberInput.label);
        this.spoNumberInputMessages = new Map(this.spoNumberInputMessages);
    }

    /**
     * Sets the new claim code number value
     * Calls {@link updatedClaimInformation.emit()} to emit new header values
     */
    setClaimCodeValue(claimCode: ClaimCodeBaseModel): void {
        this.manageRequiredAttachments(claimCode);
        this.state.claimInformationValues.claimCode = claimCode;
        this.updatedClaimInformation.emit(this.state.claimInformationValues);
    }

    /**
     * Manages the claim code input status changes, adding errors and warnings as needed
     */
    manageClaimCodeInputStateChange(claimCode: AbstractControl): void {
        this.manageFormState(claimCode);
        this.inputMessagesService.mandatoryWarnings(claimCode,
                this.claimCodeInputMessages,
                MandatoryMessageTypeEnum.SAVE_AND_SEND);
        this.inputMessagesService.invalidInputValueError(claimCode,
                this.claimCodeInputMessages,
                this.claimCodeInput.label);
        this.claimCodeInputMessages = new Map(this.claimCodeInputMessages);
        this.manageMandatoryFieldsErrorsMap(
                ClaimInformationMandatoryFieldsEnum.CLAIM_CODE,
                claimCode.value,
                claimCode.value ? claimCode.value.code : null,
                this.claimCodeInput
        );
    }

    /**
     * Sets the new subject value
     * Calls {@link updatedClaimInformation.emit()} to emit new header values
     */
    setSubjectValue(subject: string): void {
        this.state.claimInformationValues.subject = subject;
        this.updatedClaimInformation.emit(this.state.claimInformationValues);
    }

    /**
     * Manages the subject input status changes, adding errors and warnings as needed
     */
    manageSubjectChange(subject: AbstractControl): void {
        this.manageFormState(subject);
        this.inputMessagesService.mandatoryWarnings(subject,
                this.subjectInputMessages,
                MandatoryMessageTypeEnum.SEND);
        this.inputMessagesService.invalidInputValueError(subject,
                this.subjectInputMessages,
                this.subjectInput.label);
        this.subjectInputMessages = new Map(this.subjectInputMessages);
    }

    /**
     * Sets the new description value
     * Calls {@link updatedClaimInformation.emit()} to emit new header values
     */
    setDescriptionValue(description: string): void {
        this.state.claimInformationValues.description = description;
        this.updatedClaimInformation.emit(this.state.claimInformationValues);
    }

    /**
     * Manages the description input status changes, adding errors and warnings as needed
     */
    manageDescriptionChange(description: AbstractControl): void {
        this.manageFormState(description);
        this.inputMessagesService.mandatoryWarnings(description,
                this.descriptionInputMessages,
                MandatoryMessageTypeEnum.SEND);
        this.inputMessagesService.invalidInputValueError(description,
                this.descriptionInputMessages,
                this.descriptionInput.label);
        this.descriptionInputMessages = new Map(this.descriptionInputMessages);
    }

    /**
     * Claim code input filter function
     */
    searchClaimCodes = (term: string): Observable<any[]> => {
        if (term != null && term.length > 0) {
            return of(this.state.claimCodeOptions.filter(claimCode => (`${claimCode.code} - ${claimCode.name}`)
                    .toLowerCase().indexOf(term.toLowerCase()) > -1));
        }
        return of(this.state.claimCodeOptions);
    };

    /**
     * Partner input filter function
     */
    searchPartners = (term: string): Observable<any[]> => {
        if (term != null && term.length > 0) {
            return of(this.state.partnerOptions.filter(partner => (`${partner.code} - ${partner.name}`)
                    .toLowerCase().indexOf(term.toLowerCase()) > -1));
        }
        return of(this.state.partnerOptions);
    };

    /**
     * Supplying packing plant input filter function
     */
    searchSupplyingPackingPlants = (term: string): Observable<any[]> => {
        if (term != null && term.length > 0) {
            return of(this.state.supplyingPackingPlantOptions.filter(plant => (`${plant.code} - ${plant.name}`)
                    .toLowerCase().indexOf(term.toLowerCase()) > -1));
        }
        return of(this.state.supplyingPackingPlantOptions);
    };

    /**
     * Material input filter function
     */
    searchMaterials = (term: string): Observable<any[]> => {
        if (term != null && term.length > 0) {
            return this.materialsApi.getAutoCompleteMaterials(term, this.state.claimInformationValues.supplyingPackingPlant.code);
        }
        return of([]);
    };

    /**
     * Runs size and number validations and starts the upload of new file to the claim directory
     * If the file is bigger than the max size, shows warning toast
     * If no directory is already created, creates a new directory
     * Calls {@link createFileAttachment} to add the attachment to the array
     * @param file The file to be uploaded
     */
    uploadFile(file: File): void {
        if (this.invalidFileName(file.name)) {
            this.invalidFileNameCharactersToast(file.name);
        } else if (file.size > this.state.fileManagerConfig.fileMaxSize) {
            this.maxSizeExceededToast();
        } else if (this.template.attachments.length >= this.state.fileManagerConfig.directoryMaxFileCount) {
            this.maxQuantityExceededToast();
        } else {
            this.createFileAttachment(file);
        }
    }

    /**
     * Renames (if needed) and adds to {@link attachments} array a new file
     * Call {@link uploadFileCall} to make the upload file BE request
     * @param file the file to add to the attachments
     */
    createFileAttachment(file: File): void {
        this.attachmentsHaveChanged.emit(true);
        const hasSameAttachment = this.template.attachments.some(item => item.file.name === file.name);
        if (!hasSameAttachment) {
            this.sendFile(file);
        } else {
            const modal: ExistingFileModalComponent = this.modal.open(ExistingFileModalComponent, {
                centered: true
            }).componentInstance;

            const model: ExistingFileModalModel = {
                titleText: 'A file with the same name was already uploaded',
                subtitleText: [
                    `<p>A file named "${file.name}" was already uploaded.</p>`,
                    `<p><strong>Do you want to keep both files, replace it with the one you’re moving,`
                    + ` or discard the operation?</strong></p>`
                ].join('<br>'),
                alertNotificationSeverity: AlertNotificationSeverity.WARNING,
                primaryButtonText: 'Replace',
                secondaryButtonText: 'Keep both',
                tertiaryButtonText: 'Discard'
            };

            modal.initModal(model);
            this.state.onFilesPending.next();

            this.state.subscriptions.add(merge(modal.confirmEvent, modal.secondaryEvent, modal.tertiaryEvent).subscribe(() => {
                this.state.onFilesPending.next();
                this.updateDragAndDropArea();
            }));

            this.state.subscriptions.add(this.state.onFilesPending.subscribe(() => {
                modal.secondaryActionDisabled = this.template.attachments.length >= this.state.fileManagerConfig.directoryMaxFileCount;
            }));

            this.state.subscriptions.add(this.state.onFilesDownload.subscribe(() => {
                modal.primaryActionDisabled = this.template.attachments.find(a => a.file.name === file.name).isDownloading;
            }));

            modal.confirmEvent.pipe(first()).subscribe(() => {
                this.prepareFileReplace(file);
            });

            modal.secondaryEvent.pipe(first()).subscribe(() => {
                this.renameAndSendFile(file);
            });
        }
        this.updateDragAndDropArea();
    }

    updateDragAndDropArea(): void {
        this.template.disableDragAndDrop = this.template.attachments.length >= this.state.fileManagerConfig.directoryMaxFileCount;
    }

    sendFile(file: File): void {
        this.isTouched.emit(true);

        this.prepareFileUpload(file);
    }

    renameAndSendFile(file: File): void {
        let filesWithSameName = 1;
        let newFileName: string;
        do {
            newFileName = this.renameFile(file, filesWithSameName++);
        } while (this.template.attachments.some(item => item.file.name === newFileName));

        this.prepareFileUpload(file, newFileName);
    }

    prepareFileUpload(file: File, newFileName?: string): void {
        const formData = new FormData();
        formData.append('file', file, newFileName);
        const newFile = formData.get('file') as File;
        this.template.attachments.push({file: newFile});
        this.state.onFilesPending.next();
        this.updateDragAndDropArea();
        this.uploadFileCall(newFile);
    }

    prepareFileReplace(file: File): void {
        this.template.attachments = this.template.attachments.filter(a => a.file.name !== file.name);

        // Defer the call so Angular can run change detection on its own
        timer(0).subscribe(() => {
            this.prepareFileUpload(file);
        });
    }

    /**
     * Determines if the attachments section should be shown
     */
    showAttachments(): boolean {
        return this.userService.hasPermissions([
            Permission.PARTNER_DIRECTORY_CREATE,
            Permission.PARTNER_FILE_UPLOAD
        ]);
    }

    /**
     * Determines if the user has delete directory / file permissions
     */
    hasDeletePermissions(): boolean {
        return this.userService.hasPermissions([Permission.PARTNER_DIRECTORY_DELETE]);
    }

    /**
     * Discards a file, either ongoing or completed
     * @param upload the file being uploaded
     */
    discardUpload(upload: UploadFileModel): void {
        if (upload.subscription != null && !upload.subscription.closed) {
            upload.subscription.unsubscribe();
            this.template.attachments = this.template.attachments.filter(a => a !== upload);
        }

        if (upload.failed) {
            this.template.attachments = this.template.attachments.filter(a => a !== upload);
        }

        this.updateDragAndDropArea();
        this.attachmentsHaveChanged.emit(true);
        this.validForSave.emit(this.isValidForSave());
        this.validForSend.emit(this.isValidForSend());

        if (upload.finished) {
            this.isDeleting = true;
            this.state.subscriptions.add(
                    this.fileManagerApi.deleteFile(this.state.claimInformationValues.attachmentDirectory, upload.file.name)
                            .subscribe(
                                    () => {
                                        this.toast.success(`The file <strong>${upload.file.name}</strong> was deleted with success.`,
                                                'Success');
                                    },
                                    () => {
                                        this.toast.error([
                                            '<p>Due to a communication problem with the server, your request was not attended.</p>',
                                            '<p><strong>Please try again later</strong></p>',
                                        ].join('<br>'), 'Error');
                                        this.isDeleting = false;
                                    },
                                    () => {
                                        this.template.attachments = this.template.attachments.filter(a => a !== upload);
                                        this.template.disableDragAndDrop =
                                                this.template.attachments.length >= this.state.fileManagerConfig.directoryMaxFileCount;
                                        this.validForSave.emit(this.isValidForSave());
                                        this.validForSend.emit(this.isValidForSend());
                                        this.claimAttachments.emit(this.createFileSummary(this.template.attachments));
                                        this.isDeleting = false;
                                    })
            );
        }
    }

    /**
     * Calls the {@link FileDownloadHelperService} to download a specific file
     * Calls the {@link updateDownloadStatus} to update the downloadig file status
     * @param fileName The name of the file to download
     */
    downloadFile(fileName: string): void {
        this.updateDownloadStatus(fileName);
        this.fileDownloadHelperService.downloadFile(this.state.claimInformationValues.attachmentDirectory, fileName);
    }

    /**
     * test if drag and drop area should be disable
     * @return boolean condition
     */
    disableDragAndDropArea(): boolean {
        return this.state.fileManagerOffline || this.template.disableDragAndDrop;
    }

    public updateOverlayItem(item: UploadFileModel): void {
        this.removeAllOpenOverlay();
        item.overlayInProgress = true;
    }

    public removeAllOpenOverlay(): void {
        if (this.template.attachments != null) {
            this.template.attachments.forEach(item => {
                item.overlayInProgress = false;
            });
        }
    }

    /**
     * Sets the {@link UploadFileModel.isDownloading} value to true when download is started.
     * Subscribes to {@link FileDownloadHelperService.downloadIsOver} to update the {@link UploadFileModel.isDownloading} value
     * @param fileName The name of the file being downloaded
     */
    private updateDownloadStatus(fileName: string): void {
        this.template.attachments.find(file => file.file.name === fileName).isDownloading = true;
        this.state.onFilesDownload.next();
        this.fileDownloadHelperService.downloadIsOver.subscribe(status => {
            this.template.attachments.find(file => file.file.name === status.fileName).isDownloading = !status.downloadIsOver;
            this.state.onFilesDownload.next();
        });
    }

    /**
     *  Sets the label on Drag And Drop Upload Area
     *  @param fileConfig: Claim detail information
     *
     */
    private labelUploadArea = (fileConfig: FileManagerConfigurationModel): void => {
        let fileMaxUploadFile;

        if (fileConfig.fileMaxSize > 0) {
            fileMaxUploadFile = Math.round(fileConfig.fileMaxSize / (1024 * 1024));
        }
        this.uploadAreaLabel = `The maximum size per file is ${fileMaxUploadFile} MB and the maximum files ` +
                `quantity is ${fileConfig.directoryMaxFileCount}. ` +
                `Regarding the file name, a maximum length of 45 characters, consisting of only letters, digits, ` +
                `spaces and the special characters ".", "-", "_", "(", ")" is allowed.`;
    };

    /**
     * Manages the drag and drop area being enabled / disabled according to the file manager configurations
     */
    private toggleDragAndDrop(): void {
        if (this.state.fileManagerConfig == null
                || this.state.fileManagerConfig.fileMaxSize == null
                || this.state.fileManagerConfig.directoryMaxFileCount == null) {
            this.template.disableDragAndDrop = true;
        } else {
            this.template.disableDragAndDrop =
                    this.template.attachments.length >= this.state.fileManagerConfig.directoryMaxFileCount;
        }
    }

    private manageRequiredAttachments(claimCode: ClaimCodeBaseModel): void {
        if (claimCode != null && this.isValidCodeOption(claimCode)) {
            if (this.requiredAttachments !== claimCode.requiresAttachments && claimCode.requiresAttachments != null) {
                if (claimCode.requiresAttachments) {
                    this.toast.warning(`The chosen claim code <strong>requires</strong> attachments in order to be sent.`,
                            'Warning');
                } else {
                    this.toast.warning(`The chosen claim code <strong>does not require</strong> attachments in order to be sent.`,
                            'Warning');
                }
            }
            this.requiredAttachments = claimCode.requiresAttachments;
        }
    }

    /**
     * Creates the file summary for the display component
     * @param uploadedFiles The uploaded files
     * @return The uploaded files summary
     */
    private createFileSummary(uploadedFiles: UploadFileModel[]): FileSummaryModel[] {
        return uploadedFiles.map(uploadedFile => ({
                name: uploadedFile.file.name,
                size: uploadedFile.file.size
            }));
    }

    /**
     * Add the rep count to a file name
     * @param file the file to change the name
     * @param repCount the number of duplicated of the file to be added to the final string
     * @return string the new file name
     */
    private renameFile(file: File, repCount: number): string {
        const dotIndex = file.name.lastIndexOf('.');
        if (dotIndex === -1) {
            return `${file.name}(${repCount})`;
        } else {
            const startSubstring = file.name.substring(0, dotIndex);
            const endSubstring = file.name.substring(dotIndex);
            return `${startSubstring}(${repCount})${endSubstring}`;
        }
    }

    /**
     * Makes the BE call to upload a new attachment file
     */
    private uploadFileCall(file: File): void {
        const formData = new FormData();
        formData.append('file', file);
        const formDataFile = formData.get('file') as File;
        const newFile = this.template.attachments.find(item => item.file.name === formDataFile.name);

        newFile.subscription = this.fileManagerApi.uploadFile(
                this.state.claimInformationValues.attachmentDirectory, formData)
                .subscribe((response) => {
                    newFile.event = response;
                    newFile.failed = false;
                    newFile.isDownloading = false;
                    newFile.finished = response.type === HttpEventType.Response;
                    this.validForSave.emit(this.isValidForSave());
                    this.validForSend.emit(this.isValidForSend());
                    this.claimAttachments.emit(this.createFileSummary(this.template.attachments));
                }, error => {
                    newFile.event = error;
                    newFile.failed = true;
                    newFile.isDownloading = false;
                    this.validForSave.emit(this.isValidForSave());
                    this.validForSend.emit(this.isValidForSend());
                });
        this.state.subscriptions.add(newFile.subscription);
    }

    /**
     * Creates a file max size exceeded warning toast
     */
    private maxSizeExceededToast(): void {
        this.toast.warning(`The maximum file size of ${this.state.fileManagerConfig.fileMaxSize / (1024 * 1024)} MB has been exceeded. `
                + `<p><strong>Please upload a smaller file.</strong></p>`, 'Warning');
    }

    /**
     * Creates a file max quantity exceeded warning toast
     */
    private maxQuantityExceededToast(): void {
        this.toast.warning(`The maximum file quantity of ${this.state.fileManagerConfig.directoryMaxFileCount} has been achieved. `
                + `<p><strong>Please compress them in just one file, e.g. a zip file.</strong></p>`, 'Warning');
    }

    /**
     * Creates a file name have special characters warning toast
     */
    private invalidFileNameCharactersToast(fileName: string): void {
        this.toast.warning([
            `<p>The file name ${fileName} is invalid. Only letters, digits, spaces and the special characters ` +
            `".", "-", "_", "(", ")" are allowed. The maximum length must not exceed 45 characters.</p>`,
            `<strong>Please, rename the file and upload it again.</strong>`
        ].join('<br>'), 'Warning');
    }

    /**
     * Validate File Name
     */
    private invalidFileName(fileName: string): boolean {
        const reg = /[^\w\s.\-()]/;
        return reg.test(fileName) && fileName.lastIndexOf('.') >= 45 || fileName.length > 50;
    }

    /**
     * Adds entries the {@link mandatoryFieldsErrorsMap} and emits the {@link mandatoryFieldsErrors}
     * @param field the field to which to add the error
     * @param value the field value
     * @param valueCode the field code value
     * @param input the field associated input component
     */
    private manageMandatoryFieldsErrorsMap(field: ClaimInformationMandatoryFieldsEnum,
                                           value: any, valueCode: string, input: InputComponent): void {
        if (value == null || valueCode == null) {
            this.mandatoryFieldsErrorsMap.set(field, TooltipEnum.WARNING);
        } else if (!input.isValid) {
            this.mandatoryFieldsErrorsMap.set(field, TooltipEnum.ERROR);
        } else {
            this.mandatoryFieldsErrorsMap.delete(field);
        }
        this.mandatoryFieldsErrors.emit(this.mandatoryFieldsErrorsMap);
    }

    /**
     * Gets the ship to party options
     * @param partner The selected partner code
     */
    private getSupplyingPackingPlantOptions = (partner: string): void => {
        this.isLoading.emit(true);
        this.state.subscriptions.add(
                this.referenceDataApi.getActiveSupplyingPackingPlants(partner)
                        .subscribe((options: SupplyingPackingPlantBaseModel[]) => {
                            this.state.supplyingPackingPlantOptions = options;
                            if (this.state.supplyingPackingPlantOptions.length === 1 && !this.supplyingPackingPlantInput.hasValue()) {
                                this.supplyingPackingPlantInput.setValue(options[0]);
                            }
                            if (this.supplyingPackingPlantInput.isValid
                                    && this.isValidCodeOption(this.state.claimInformationValues.supplyingPackingPlant)) {
                                if (!options.some(option =>
                                        option.code === this.state.claimInformationValues.supplyingPackingPlant.code)) {
                                    this.supplyingPackingPlantInput.markAsInvalid();
                                }
                            }
                        }).add(() => {
                    this.isLoading.emit(false);
                }));
    };

    /**
     * Manages the material input value and disabled / enabled status after supplying packing plant change
     */
    private manageMaterialInputAfterPlantChange(): void {
        const material = this.state.claimInformationValues.material;
        const plant = this.state.claimInformationValues.supplyingPackingPlant;
        if (this.isValidCodeOption(plant) && !this.claimCodeInput.isDisabled) {
            this.materialInput.enable();
            this.materialInputMessages.delete(WarningsEnum.WARNING);
            if (material != null && material.number != null) {
                this.validateMaterial(material.number, plant.code);
            }
        }
    }

    /**
     * Validates a material from a specific supplying packing plant
     * @param materialNumber The material number to be validated
     * @param plantCode The plant code for which to check if the material is valid
     */
    private validateMaterial(materialNumber: string, plantCode: string): void {
        this.isLoading.emit(true);
        this.state.subscriptions.add(
                this.materialsApi.validateMaterial(materialNumber, plantCode).subscribe(validation => {
                    if (!validation.valid) {
                        this.materialInput.markAsInvalid();
                    }
                }).add(() => {
                            this.isLoading.emit(false);
                        }
                ));
    }

    /**
     * Manages the claim code input after parent change
     */
    private manageClaimCodeInputAfterParentChange(): void {
        if (this.supplyingPackingPlantInput.isValid && this.partnerInput.isValid
                && this.isValidCodeOption(this.state.claimInformationValues.supplyingPackingPlant)
                && this.isValidCodeOption(this.state.claimInformationValues.partner)) {
            this.claimCodeInputMessages.delete(WarningsEnum.WARNING);
            this.claimCodeInput.enable();
            this.getClaimCodeOptions(this.state.claimInformationValues.partner.code,
                    this.state.claimInformationValues.supplyingPackingPlant.code);
        }
    }

    private supplyingPackingPlantAfterParentChange(): void {
        if (this.partnerInput.isValid && this.isValidCodeOption(this.state.claimInformationValues.partner)) {
            this.supplyingPackingPlantInput.enable();
            this.supplyingPackingPlantInputMessages.delete(WarningsEnum.WARNING);
            this.getSupplyingPackingPlantOptions(this.state.claimInformationValues.partner.code);
        }
        this.manageClaimCodeInputAfterParentChange();
    }

    /**
     * Retrieves the claim code options for the dropdown
     * @param partnerCode The partner code
     * @param plantCode The supplying packing plant code
     */
    private getClaimCodeOptions(partnerCode: string, plantCode: string): void {
        this.isLoading.emit(true);
        this.state.subscriptions.add(
                this.referenceDataApi.getActiveClaimCodes(partnerCode, plantCode)
                        .subscribe((claimCodes: ClaimCodeBaseModel[]) => {
                            this.state.claimCodeOptions = claimCodes;
                            if (!this.claimCodeInput.isDisabled && this.isValidCodeOption(this.state.claimInformationValues.claimCode)) {
                                if (!claimCodes.some(claimCode =>
                                        claimCode.code === this.state.claimInformationValues.claimCode.code)) {
                                    this.claimCodeInput.markAsInvalid();
                                }
                            }
                        }).add(() => {
                    this.isLoading.emit(false);
                }));
    }

    /**
     * Disables the dependent inputs and sets them with a father invalid warning
     * @param parent The input which the value has changed
     * @param parentValue The new input value
     */
    private dependencyCheck(parent: InputComponent, parentValue: any): void {
        const inputsMap = new Map<InputComponent, { input: InputComponent, messages: Map<WarningsEnum, string[]> }[]>([

            [this.partnerInput, [
                {input: this.supplyingPackingPlantInput, messages: this.supplyingPackingPlantInputMessages},
                {input: this.materialInput, messages: this.materialInputMessages},
                {input: this.claimCodeInput, messages: this.claimCodeInputMessages}]
            ],
            [this.supplyingPackingPlantInput, [
                {input: this.materialInput, messages: this.materialInputMessages},
                {input: this.claimCodeInput, messages: this.claimCodeInputMessages}]
            ]
        ]);
        if (!parent.isValid || parentValue == null) {
            inputsMap.get(parent).forEach(child => {
                this.inputMessagesService.fatherInputValueWarning(parent, child.input, child.messages);
                child.messages = new Map(child.messages);
            });
        }
    }

    /**
     * Checks if the form is valid for save
     * @returns boolean if the global form is valid for save
     */
    private isValidForSave(): boolean {
        return this.formIsValid()
                && this.state.claimInformationValues.partner != null
                && this.state.claimInformationValues.partner.code != null
                && this.state.claimInformationValues.supplyingPackingPlant != null
                && this.state.claimInformationValues.supplyingPackingPlant.code != null
                && this.state.claimInformationValues.material != null
                && this.state.claimInformationValues.material.number != null
                && this.state.claimInformationValues.claimCode != null
                && this.state.claimInformationValues.claimCode.code != null;
    }

    /**
     * Checks if the form is valid for send
     * @returns boolean if the global form is valid for send
     */
    private isValidForSend(): boolean {
        return this.formIsValid()
                && this.isValidForSave()
                && this.hasRequiredAttachments()
                && this.state.claimInformationValues.subject != null
                && this.state.claimInformationValues.subject !== ''
                && this.state.claimInformationValues.description != null
                && this.state.claimInformationValues.description !== '';
    }

    /**
     * Checks if the required attachments are present. If attachments are not required returns always true
     * @returns boolean if attachments are required and at least one file was uploaded
     */
    private hasRequiredAttachments(): boolean {
        if (this.state.claimInformationValues.claimCode.requiresAttachments) {
            return this.template.attachments.length > 0;
        }
        return true;
    }

    /**
     * Checks if every input is valid
     * @returns boolean if all the inserted inputs values are valid
     */
    private formIsValid(): boolean {
        return this.partnerInput.isValid
                && this.supplyingPackingPlantInput.isValid
                && this.materialInput.isValid
                && this.modelCodeInput.isValid
                && this.modelTypeInput.isValid
                && this.lotNumberInput.isValid
                && this.mcdrNumberInput.isValid
                && this.claimCodeInput.isValid
                && this.subjectInput.isValid
                && this.spoNumberInput.isValid
                && this.descriptionInput.isValid
                && this.attachmentsAreValid();
    }

    private attachmentsAreValid(): boolean {
        return this.template.attachments.filter(item => !item.finished || item.failed).length === 0;
    }

    private isValidCodeOption = (option): boolean => option !== '' && option != null && option.code != null;
}
